/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Form, Grid } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { get, filter, format as _format } from 'lodash';

import { FormLabel } from '../../../../components';
import MasterServiceFields from '../../containers/form/MasterServiceFields';
import { MASTER_SERVICE_FIELDS_FRAGMENT } from '../../containers/masterAccessQueries';

// flow types
import type { Intl } from '../../../../type';

export const GET_CABINETS_QUERY = gql`
    query getMasters(
        $salon: ID!
        $date: String!
        $lang: String!
        $showArchive: Boolean!
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                    }
                    masters(date: $date) {
                        nodes {
                            id
                            firstName
                            lastName
                            schedule(startDate: $date) {
                                id
                                startAt
                                endAt
                            }
                            freeWorkingTime(
                                date: $date
                                showArchive: $showArchive
                            ) {
                                startAt
                                endAt
                            }
                            serviceGroups {
                                id
                                name(lang: $lang)
                                services {
                                    id
                                    name(lang: $lang)
                                    duration
                                    price
                                }
                                cabinets {
                                    id
                                    name(lang: $lang)
                                    schedule(startDate: $date) {
                                        startAt
                                        endAt
                                    }
                                    freeWorkingTime(
                                        date: $date
                                        showArchive: $showArchive
                                    ) {
                                        startAt
                                        endAt
                                    }
                                }
                            }
                        }
                    }
                }
            }
            ...onMasterServiceFields
        }
    }
    ${MASTER_SERVICE_FIELDS_FRAGMENT}
`;

const intlMessages = {
    master: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.masterPlaceholder',
            defaultMessage: 'Master',
        },
    }),
    service: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.servicePlaceholder',
            defaultMessage: 'Service',
        },
    }),
    startTime: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.startTimePlaceholder',
            defaultMessage: 'Time',
        },
    }),
    duration: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.durationPlaceholder',
            defaultMessage: 'Duration',
        },
    }),
};

type ServiceFieldsListProps = {
    readOnly: boolean,
    intl: Intl,
    defaultMaster: String,
    withLabel: boolean,
    multiple: boolean,
};

const StyledGrid = styled(Grid)`
    padding-left: 0.5em !important;
    padding-right: 0.5em !important;
`;

const StyledColumn = styled(Grid.Column)`
    padding-left: 0.5em !important;
    padding-right: 0.5em !important;
    label.required {
        &::after {
            margin: -0.2em 0 0 0.2em;
            content: '*';
            color: #db2828;
        }
    }
`;

const FirstRow = styled(Grid.Row)`
    padding-bottom: 0px !important;
    padding-right: 52px !important;
`;

const LastRow = styled(Grid.Row)`
    padding-top: 0px !important;
`;

function MasterServiceFieldList({
    readOnly,
    intl,
    defaultMaster,
    withLabel,
    multiple,
}: ServiceFieldsListProps) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({ control, name: 'services' });

    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(({ intl: i }) => i.get('locale') || i.get('defaultLanguage'));
    const showArchive = useSelector(({ salon: s }) => s.get('showArchive'));

    const startDate = useWatch({ control, name: 'startDate' });

    const { data, loading } = useQuery(GET_CABINETS_QUERY, {
        variables: {
            salon,
            date: startDate || new Date().toISOString().slice(0, 10),
            lang: currentLanguage,
            showArchive,
        },
        fetchPolicy: 'cache-and-network',
        skip: !salon,
    });

    const masters =
        !loading && data && data.viewer
            ? filter(
                  get(data, 'viewer.salon.masters.nodes', []),
                  ({ schedule }) => schedule && schedule.length,
              )
            : [];

    return (
        <StyledGrid>
            {withLabel && (
                <FirstRow>
                    <StyledColumn width={5}>
                        <FormLabel className="required">
                            {intl.formatMessage(intlMessages.master.placeholder)}
                        </FormLabel>
                    </StyledColumn>
                    <StyledColumn width={5}>
                        <FormLabel className="required">
                            {intl.formatMessage(intlMessages.service.placeholder)}
                        </FormLabel>
                    </StyledColumn>
                    <StyledColumn width={3}>
                        <FormLabel className="required">
                            {intl.formatMessage(intlMessages.duration.placeholder)}
                        </FormLabel>
                    </StyledColumn>
                    <StyledColumn width={3}>
                        <FormLabel className="required">
                            {intl.formatMessage(intlMessages.startTime.placeholder)}
                        </FormLabel>
                    </StyledColumn>
                </FirstRow>
            )}
            <LastRow>
                <StyledColumn width={16}>
                    {fields.map((fieldItem, idx) => (
                        <Form.Field key={fieldItem.id}>
                            <MasterServiceFields
                                onRemove={() => remove(idx)}
                                index={idx}
                                readOnly={readOnly}
                                withLabel={false}
                                masters={masters}
                                disableRemove={withLabel && idx === 0}
                                loading={loading}
                            />
                        </Form.Field>
                    ))}
                    <a
                        href="#add"
                        onClick={e => {
                            e.preventDefault();
                            append(defaultMaster ? { master: defaultMaster } : {});
                        }}
                    >
                        +{' '}
                        <FormattedMessage
                            id="pages.appointments.form.ServiceFieldsList.add_service"
                            defaultMessage="Add Service"
                        />
                    </a>
                </StyledColumn>
            </LastRow>
        </StyledGrid>
    );
}

export default injectIntl(MasterServiceFieldList);
